import { supabase } from '@/integrations/supabase/client';

export interface FilingJob {
  id: string;
  company_id: string;
  filing_type: string;
  period_start: string | null;
  period_end: string | null;
  status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED';
  output_zip_url: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface StatPackData {
  shareholder_register: any[];
  allotment_register: any[];
  transfer_register: any[];
  minutes_items: any[];
  certificates: any[];
  latest_valuation: any[];
}

/**
 * Get all filing jobs for a company
 */
export async function getFilingJobs(companyId: string): Promise<FilingJob[]> {
  const { data, error } = await supabase
    .from('filing_jobs')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching filing jobs:', error);
    throw error;
  }

  return (data || []) as FilingJob[];
}

/**
 * Create a new filing job
 */
export async function createFilingJob(
  companyId: string,
  filingType: string,
  periodStart: string | null,
  periodEnd: string | null,
  notes: string | null
): Promise<FilingJob> {
  const { data, error } = await supabase
    .from('filing_jobs')
    .insert({
      company_id: companyId,
      filing_type: filingType,
      period_start: periodStart,
      period_end: periodEnd,
      notes,
      created_by: (await supabase.auth.getUser()).data.user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating filing job:', error);
    throw error;
  }

  return data as FilingJob;
}

/**
 * Collect statutory pack data using RPC
 */
export async function collectStatPack(
  companyId: string,
  periodStart: string | null = null,
  periodEnd: string | null = null
): Promise<StatPackData> {
  const { data, error } = await supabase.rpc('collect_stat_pack', {
    p_company: companyId,
    p_from: periodStart,
    p_to: periodEnd,
  });

  if (error) {
    console.error('Error collecting stat pack:', error);
    throw error;
  }

  return data as unknown as StatPackData;
}

/**
 * Update filing job status
 */
export async function updateFilingJob(
  jobId: string,
  status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED',
  outputZipUrl: string | null = null
): Promise<void> {
  const updateData: any = {
    status,
  };

  if (outputZipUrl) {
    updateData.output_zip_url = outputZipUrl;
  }

  if (status === 'SUCCESS' || status === 'FAILED') {
    updateData.finished_at = new Date().toISOString();
  }

  const { error } = await supabase.from('filing_jobs').update(updateData).eq('id', jobId);

  if (error) {
    console.error('Error updating filing job:', error);
    throw error;
  }
}

/**
 * Download stat pack data as JSON file
 */
export function downloadStatPack(data: StatPackData, companyName: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${companyName}-statutory-pack-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate filing pack using edge function
 */
export async function generateFilingPack(
  companyId: string,
  filingType: string,
  periodStart: string | null,
  periodEnd: string | null,
  jobId: string
): Promise<{ manifestUrl: string; files: Record<string, string> }> {
  const { data, error } = await supabase.functions.invoke('generate-filing-pack', {
    body: {
      companyId,
      filingType,
      periodStart,
      periodEnd,
      jobId,
    },
  });

  if (error) {
    console.error('Error generating filing pack:', error);
    throw error;
  }

  return data;
}

/**
 * Generate CoR39 form for a company
 */
export async function generateCoR39(
  companyId: string
): Promise<{ success: boolean; url?: string; filename?: string; error?: string }> {
  const { data, error } = await supabase.functions.invoke('generate-cor39', {
    body: { companyId },
  });

  if (error) {
    console.error('Error generating CoR39:', error);
    throw error;
  }

  return data;
}

/**
 * Generate CoR15.2 form for a company
 */
export async function generateCoR15_2(
  companyId: string
): Promise<{ success: boolean; url?: string; filename?: string; error?: string }> {
  const { data, error } = await supabase.functions.invoke('generate-cor15-2', {
    body: { companyId },
  });

  if (error) {
    console.error('Error generating CoR15.2:', error);
    throw error;
  }

  return data;
}
